// Roadmap editor: direct edits (persisted immediately), natural-language
// refine suggestions, and confirmation.

import React from "react";
import type { ChartKind, Roadmap, RoadmapUnit } from "@asmg/shared";
import { CHART_KINDS } from "@asmg/shared";
import { api } from "../api/client";
import { Button, ErrorBanner, Spinner } from "../components/ui";

interface Props {
  projectId: string;
  roadmap: Roadmap;
  confirmed: boolean;
  onRoadmapChange: (r: Roadmap) => void;
  onConfirmed: () => void;
}

export function RoadmapEditor({ projectId, roadmap, confirmed, onRoadmapChange, onConfirmed }: Props) {
  const [suggestion, setSuggestion] = React.useState("");
  const [busy, setBusy] = React.useState<null | "refine" | "save" | "confirm">(null);
  const [error, setError] = React.useState<unknown>(null);

  // Persist a direct edit immediately (R3.3).
  async function persist(next: Roadmap) {
    onRoadmapChange(next); // optimistic
    setBusy("save");
    setError(null);
    try {
      const saved = await api.refineRoadmap(projectId, { roadmap: next });
      onRoadmapChange(saved);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  function updateUnit(idx: number, patch: Partial<RoadmapUnit>) {
    const units = roadmap.units.map((u, i) => (i === idx ? { ...u, ...patch } : u));
    void persist({ units });
  }

  function addUnit() {
    const units = [
      ...roadmap.units,
      {
        id: "",
        ord: roadmap.units.length,
        title: "New Unit",
        subsections: [],
        plannedCharts: [],
        plannedTables: [],
      } satisfies RoadmapUnit,
    ];
    void persist({ units });
  }

  function removeUnit(idx: number) {
    const units = roadmap.units.filter((_, i) => i !== idx).map((u, i) => ({ ...u, ord: i }));
    void persist({ units });
  }

  async function applySuggestion() {
    const s = suggestion.trim();
    if (!s) return;
    setBusy("refine");
    setError(null);
    try {
      const revised = await api.refineRoadmap(projectId, { suggestion: s });
      onRoadmapChange(revised);
      setSuggestion("");
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  async function confirm() {
    setBusy("confirm");
    setError(null);
    try {
      await api.confirmRoadmap(projectId);
      onConfirmed();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <div className="card">
        <label htmlFor="suggest">Suggest a change (AI refine)</label>
        <textarea
          id="suggest"
          placeholder="e.g. Add a unit on statutory compliance; split unit 2 into two"
          value={suggestion}
          onChange={(e) => setSuggestion(e.target.value)}
        />
        <div className="row" style={{ marginTop: 8 }}>
          <Button onClick={applySuggestion} loading={busy === "refine"} disabled={!suggestion.trim()}>
            Apply Suggestion
          </Button>
          <Button variant="ghost" onClick={addUnit} disabled={!!busy}>
            + Add Unit
          </Button>
          {busy === "save" && <Spinner label="Saving…" />}
        </div>
      </div>

      <ErrorBanner error={error} onRetry={() => setError(null)} />

      {roadmap.units.map((unit, idx) => (
        <div className="card unit-card" key={unit.id || idx}>
          <div className="row-between">
            <strong>Unit {idx + 1}</strong>
            <Button variant="danger" onClick={() => removeUnit(idx)} disabled={!!busy}>
              Remove
            </Button>
          </div>
          <label>Title</label>
          <input
            value={unit.title}
            onChange={(e) => onRoadmapChange({ units: patchUnit(roadmap.units, idx, { title: e.target.value }) })}
            onBlur={() => updateUnit(idx, { title: unit.title })}
          />
          <label>Subtitle</label>
          <input
            value={unit.subtitle ?? ""}
            onChange={(e) => onRoadmapChange({ units: patchUnit(roadmap.units, idx, { subtitle: e.target.value }) })}
            onBlur={() => updateUnit(idx, { subtitle: unit.subtitle })}
          />

          <label>Sub-sections</label>
          {unit.subsections.map((s, si) => (
            <div className="row" key={si} style={{ marginBottom: 6 }}>
              <input
                className="grow"
                value={s.title}
                placeholder="Section title"
                onChange={(e) => {
                  const subsections = unit.subsections.map((x, j) => (j === si ? { ...x, title: e.target.value } : x));
                  onRoadmapChange({ units: patchUnit(roadmap.units, idx, { subsections }) });
                }}
                onBlur={() => updateUnit(idx, { subsections: unit.subsections })}
              />
              <Button variant="ghost" onClick={() => updateUnit(idx, { subsections: unit.subsections.filter((_, j) => j !== si) })}>
                ✕
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            onClick={() => updateUnit(idx, { subsections: [...unit.subsections, { title: "", description: "" }] })}
          >
            + Sub-section
          </Button>

          <label style={{ marginTop: 10 }}>Planned charts</label>
          {unit.plannedCharts.map((c, ci) => (
            <div className="row" key={ci} style={{ marginBottom: 6 }}>
              <select
                value={c.kind}
                onChange={(e) => {
                  const plannedCharts = unit.plannedCharts.map((x, j) =>
                    j === ci ? { ...x, kind: e.target.value as ChartKind } : x
                  );
                  void updateUnit(idx, { plannedCharts });
                }}
                style={{ width: 140 }}
              >
                {CHART_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
              <input
                className="grow"
                value={c.title}
                placeholder="Chart title"
                onChange={(e) => {
                  const plannedCharts = unit.plannedCharts.map((x, j) => (j === ci ? { ...x, title: e.target.value } : x));
                  onRoadmapChange({ units: patchUnit(roadmap.units, idx, { plannedCharts }) });
                }}
                onBlur={() => updateUnit(idx, { plannedCharts: unit.plannedCharts })}
              />
              <Button variant="ghost" onClick={() => updateUnit(idx, { plannedCharts: unit.plannedCharts.filter((_, j) => j !== ci) })}>
                ✕
              </Button>
            </div>
          ))}
          <Button
            variant="ghost"
            onClick={() =>
              updateUnit(idx, { plannedCharts: [...unit.plannedCharts, { kind: "bar", title: "", description: "" }] })
            }
          >
            + Chart
          </Button>

          <div style={{ marginTop: 10 }}>
            {unit.plannedTables.map((t, ti) => (
              <span className="tag" key={ti}>
                🗂 {t.title || "table"}
              </span>
            ))}
          </div>
        </div>
      ))}

      <div className="card row-between">
        <span className="muted">
          {confirmed ? "Roadmap confirmed." : "Confirm the roadmap to generate the full document."}
        </span>
        <Button onClick={confirm} loading={busy === "confirm"}>
          {confirmed ? "Re-confirm" : "Confirm Roadmap"}
        </Button>
      </div>
    </div>
  );
}

function patchUnit(units: RoadmapUnit[], idx: number, patch: Partial<RoadmapUnit>): RoadmapUnit[] {
  return units.map((u, i) => (i === idx ? { ...u, ...patch } : u));
}
